import { useCallback, useMemo } from 'react';

import {
  useOperationGetAvailableStatuses,
  useOperationSaveStatus,
} from 'api/requests';
import { useServerErrorNotify } from 'hooks';
import { FormSubmit } from 'libs/form';

import { ChangeStatusFormType } from '../forms/changeStatusForm';
import { OperationAvailableStatusesMap } from '../types';

interface Props {
  operationId: string;
  versionNumber?: number;
  onSuccessCallback: () => void;
}

export const useChangeStatus = ({
  operationId,
  versionNumber,
  onSuccessCallback,
}: Props) => {
  const { loading, availableStatuses } =
    useOperationGetAvailableStatuses(operationId);

  const {
    error,
    loading: submitLoading,
    saveStatus,
  } = useOperationSaveStatus();

  const availableStatusesMap = useMemo(
    () =>
      availableStatuses.reduce((acc, status) => {
        acc[status] = {
          isRequiredReason: false,
        };
        return acc;
      }, {} as OperationAvailableStatusesMap),
    [availableStatuses]
  );

  const onSubmit: FormSubmit<ChangeStatusFormType> = useCallback(
    async (values) => {
      if (versionNumber) {
        const status = values.status.value;
        const chosenStatus = availableStatusesMap[status];

        await saveStatus(operationId, versionNumber, {
          value: status,
          reason: chosenStatus.isRequiredReason ? values.reason! : undefined,
        });

        onSuccessCallback();
      }
    },
    [
      operationId,
      versionNumber,
      availableStatusesMap,
      saveStatus,
      onSuccessCallback,
    ]
  );

  useServerErrorNotify(error);

  return {
    loading,
    submitLoading,
    availableStatusesMap,
    onSubmit,
  };
};
